import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import {
  TrailerDeckHeightField,
  TrailerEmptyWeightField,
  TrailerGroundClearanceField,
  TrailerHeightField,
  TrailerKingpinSetbackLengthField,
  TrailerKingpinSettingLengthField,
  TrailerLengthField,
  TrailerRegisteredWeightField,
  TrailerWheelbaseLengthField,
  TrailerWidthField,
} from '@src/permitField/TrailerDimensionFields';
import {
  TrailerLicensePlateField,
  TrailerLicensePlateStateField,
  TrailerLicensePlateTypeField,
  TrailersFormName,
} from '@src/permitField/TrailerInformationFields';
import { TrailerDimensionsForm } from '@src/trailer/service/TrailerDimensionsForm';
import type { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import { TrailerInformationForm } from '@src/trailer/TrailerInformationForm';
import { TrailerLicensePlateForm } from '@src/trailer/TrailerLicensePlateForm';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

interface Props {
  control: Control<yup.InferType<typeof TrailerFormSchema>>;
  readonly?: boolean;
  trailerIndex?: number;
  fields: string[];
}

export const TrailerForm = ({
  control,
  trailerIndex,
  readonly,
  fields,
}: Props) => {
  const { t } = useTranslation();

  const trailerFieldPrefix =
    trailerIndex !== undefined
      ? `${TrailersFormName}.${trailerIndex}`
      : undefined;

  if (fields.length <= 0) {
    return null;
  }

  const licenseSectionFields: string[] = [
    TrailerLicensePlateField.fieldName,
    TrailerLicensePlateStateField.fieldName,
    TrailerLicensePlateTypeField.fieldName,
  ];

  const showLicenseSection = fields.some((field) =>
    licenseSectionFields.includes(field),
  );

  const dimensionSectionFields: string[] = [
    TrailerLengthField.fieldName,
    TrailerWidthField.fieldName,
    TrailerHeightField.fieldName,
    TrailerWheelbaseLengthField.fieldName,
    TrailerDeckHeightField.fieldName,
    TrailerGroundClearanceField.fieldName,
    TrailerKingpinSetbackLengthField.fieldName,
    TrailerKingpinSettingLengthField.fieldName,
    TrailerEmptyWeightField.fieldName,
    TrailerRegisteredWeightField.fieldName,
  ];
  const showDimensionSection = fields.some((field) =>
    dimensionSectionFields.includes(field),
  );

  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      <TrailerInformationForm
        control={control}
        readonly={readonly}
        fieldsPrefix={trailerFieldPrefix}
        fields={fields}
      />

      {showLicenseSection && (
        <Stack
          aria-labelledby={`trailerLicensePlateSection-${trailerIndex ?? 'new'}`}
          gap={theme.spacing['3']}
        >
          <Typography
            id={`trailerLicensePlateSection-${trailerIndex ?? 'new'}`}
            component="h3"
            variant="h6"
          >
            {t('trailerLicensePlateSection', {
              number: trailerIndex !== undefined ? trailerIndex + 1 : '',
            })}
          </Typography>
          <TrailerLicensePlateForm
            control={control}
            fieldsPrefix={trailerFieldPrefix}
            fields={fields}
          />
        </Stack>
      )}

      {showDimensionSection && (
        <Stack
          aria-labelledby={`trailerDimensionsSection-${trailerIndex ?? 'new'}`}
          gap={theme.spacing['3']}
        >
          <Typography
            id={`trailerDimensionsSection-${trailerIndex ?? 'new'}`}
            component="h3"
            variant="h6"
          >
            {t('trailerDimensionsSection', {
              number: trailerIndex !== undefined ? trailerIndex + 1 : '',
            })}
          </Typography>
          <TrailerDimensionsForm
            control={control}
            fieldsPrefix={trailerFieldPrefix}
            fields={fields}
          />
        </Stack>
      )}
    </Stack>
  );
};
